// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { FC, useState } from "react";
import {
  Button,
  Box,
  Column,
  TextInput,
  Text,
  SendMessageIcon,
} from "@hightouch/htui";
import { Link } from "src/router";
import { captureException } from "src/lib/monitoring";
import { useNavigate } from "src/router";
import { AuthLayout } from "src/components/auth/auth-layout";
import { Terms } from "src/components/auth/terms";
import { useAuthState } from "src/components/auth/use-auth-state";
import { PartnerConnectHeader } from "src/pages/partner-connect";
import { AuthCard } from "src/components/auth/auth-card";
import { OtherOptions } from "src/components/auth/other-options";
import { buildReturnToUrl } from "src/utils/auth-redirect";
import { TOO_MANY_ATTEMPTS_MESSAGE } from "src/components/auth/util";

export const Passwordless: FC = () => {
  const { returnTo, partnerInformation, isSignup } = useAuthState();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [rateLimited, setRateLimited] = useState<boolean>(false);

  const submit = async (event: React.FormEvent<HTMLDivElement>) => {
    event.preventDefault();
    setLoading(true);
    setRateLimited(false);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            returnTo,
          }),
        },
      );
      if (response.ok) {
        setSuccess(true);
      } else if (response.status === 429) {
        setRateLimited(true);
      } else {
        setError(true);
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  };

  const route = isSignup ? "/signup" : "/login";
  const action = isSignup ? "sign up" : "log in";

  const headingText = error
    ? "Enterprise email"
    : success
      ? "Check your inbox to get started"
      : `Receive a link to ${action}`;

  const description = error ? (
    <>
      This email is tied to an enterprise organization.
      <br />
      Go to{" "}
      <Link href={returnTo ? buildReturnToUrl("/sso", returnTo) : "/sso"}>
        enterprise login
      </Link>{" "}
      to continue.
    </>
  ) : success ? (
    <>
      We’ve sent a secure sign-in link to{" "}
      <Box as="strong" color="text.primary" fontWeight="semibold">
        {email ?? "your email"}
      </Box>
    </>
  ) : (
    "Enter your email for a secure link to start using Hightouch."
  );

  return (
    <AuthLayout>
      <AuthCard heading={headingText} gap={4}>
        {partnerInformation && (
          <PartnerConnectHeader partnerInformation={partnerInformation} />
        )}

        <Text color="text.tertiary" fontWeight="medium">
          {description}
        </Text>

        {!error && !success && (
          <Column as="form" gap={4} onSubmit={submit}>
            <>
              <TextInput
                autoFocus
                name="email"
                placeholder="name@company.com"
                type="email"
                width="100%"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />

              {rateLimited && (
                <Box as={Text} color="danger.base" maxWidth="60ch">
                  {TOO_MANY_ATTEMPTS_MESSAGE}
                </Box>
              )}

              <Box sx={{ svg: { boxSize: "16px" } }}>
                <Button
                  isJustified
                  isLoading={loading}
                  icon={SendMessageIcon}
                  size="md"
                  type="submit"
                  variant="primary"
                >
                  Send link
                </Button>
              </Box>
            </>
          </Column>
        )}

        <Column mt={error || success ? 2 : 6}>
          <Terms />
        </Column>

        <OtherOptions
          variant="button"
          onClick={() => {
            navigate(returnTo ? buildReturnToUrl(route, returnTo) : route);
          }}
        />
      </AuthCard>
    </AuthLayout>
  );
};
